import type { Layout } from '../../data/types';
import type { Ring, Vec2 } from '../../geo/types';
import { distancePointSegment } from '../../geo/polygon';
import committed from '../../data/quayGoods.json';
import { streetFrame, type StreetFrame } from './streetFrame';

/*
 * The goods Anthonisz's 1544 woodcut draws on the Oudezijds Voorburgwal quays and on the IJ quay,
 * read on the scan and put in layout metres by tools/quayGoods (docs/plans/streetscape.md section
 * 6.3). The Keur of March 1501 regulates the timber stacked on the burgwal quays, between the stone
 * street and the revetment.
 */
export const QUAY_GOODS_KINDS = [
  'boxStack',
  'plankPile',
  'plankFrame',
  'crate',
  'trestle',
] as const;
export type QuayGoodsKind = (typeof QUAY_GOODS_KINDS)[number];

export interface QuayGoodsRecord {
  readonly id: string;
  readonly kind: QuayGoodsKind;
  readonly street: string;
  /** The layout point under the middle of the item's foot. */
  readonly at: Vec2;
  /** Where its middle is drawn across the quay: 0 at the water line, 1 at the lot front. */
  readonly across: number;
  readonly width: number;
  readonly length: number;
  readonly height: number;
  /** A frame's plank width. */
  readonly bar?: number;
  /** A trestle's beam height; its `width` is the beam's length across the quay, its `height` the post's. */
  readonly beam?: number;
  /** Where a trestle's forked prop stands along its beam, from the bank end (0) to the lot end (1). */
  readonly fork?: number;
  readonly sourceRefs: readonly string[];
}

/** An item on its quay: a box `length` along the quay by `width` across it, turned by `yaw`. */
export interface PlacedGoods {
  readonly id: string;
  /** The finite quay segment selected after source banks are split by restored waterways. */
  readonly street: string;
  readonly kind: QuayGoodsKind;
  readonly centre: Vec2;
  readonly yaw: number;
  readonly length: number;
  readonly width: number;
  readonly height: number;
  readonly bar?: number;
  readonly beam?: number;
  readonly fork?: number;
  /** The side of the item the bank lies on: 1 to the right of its yaw, -1 to the left. */
  readonly bankSide: 1 | -1;
  readonly footprint: Ring;
}

type Street = Layout['streets'][number];

/* Goods stand this far in from the bank: the capping beam (0.18 m, quayTimbers.ts) and its posts
   hold the bank's edge, and the layout's bank lies within a few centimetres of the water's. */
export const BANK_CLEARANCE_M = 0.3;
// Items parted along the quay keep this gap, so no two faces share a plane.
export const PART_GAP_M = 0.05;

function fail(message: string): never {
  throw new Error(`quayGoods.json: ${message}`);
}

function isPositive(v: unknown): v is number {
  return typeof v === 'number' && Number.isFinite(v) && v > 0;
}

function checkTrestle(name: string, r: Partial<Record<keyof QuayGoodsRecord, unknown>>): void {
  if (!isPositive(r.beam) || r.beam >= (r.height as number))
    fail(`${name} is a trestle with no beam below its post's head`);
  const fork = r.fork;
  if (typeof fork !== 'number' || fork < 0 || fork > 1) fail(`${name} is a trestle with no fork`);
}

function parseRecord(value: unknown, index: number): QuayGoodsRecord {
  const r = (value ?? {}) as Partial<Record<keyof QuayGoodsRecord, unknown>>;
  const name = typeof r.id === 'string' ? r.id : `item ${String(index)}`;
  const kind = QUAY_GOODS_KINDS.find((k) => k === r.kind);
  if (kind === undefined) fail(`${name} has no known kind`);
  if (typeof r.street !== 'string') fail(`${name} names no street`);
  const at = r.at;
  if (!Array.isArray(at) || at.length !== 2 || !at.every((v) => typeof v === 'number'))
    fail(`${name} has no layout point at`);
  if (typeof r.across !== 'number' || !Number.isFinite(r.across)) fail(`${name} has no across`);
  for (const key of ['width', 'length', 'height'] as const) {
    if (!isPositive(r[key])) fail(`${name} has no positive ${key}`);
  }
  if (kind === 'plankFrame' && !isPositive(r.bar)) fail(`${name} is a frame with no bar`);
  if (kind === 'trestle') checkTrestle(name, r);
  return {
    id: name,
    kind,
    street: r.street,
    at: [at[0] as number, at[1] as number],
    across: r.across,
    width: r.width as number,
    length: r.length as number,
    height: r.height as number,
    ...(isPositive(r.bar) ? { bar: r.bar } : {}),
    ...(kind === 'trestle' ? { beam: r.beam as number, fork: r.fork as number } : {}),
    sourceRefs: Array.isArray(r.sourceRefs) ? r.sourceRefs.map(String) : [],
  };
}

export function parseQuayGoods(value: unknown): QuayGoodsRecord[] {
  const items = (value as { items?: unknown } | null)?.items;
  if (!Array.isArray(items)) fail('has no items');
  return items.map(parseRecord);
}

export const QUAY_GOODS: readonly QuayGoodsRecord[] = parseQuayGoods(committed);

/** A quay goods stand on: metres along it, and across it from its bank toward the lots. */
export interface GoodsQuay {
  readonly street: string;
  readonly frame: StreetFrame;
  /** t at the bank, and the step in t from the bank toward the lots. */
  readonly bank: number;
  readonly inward: 1 | -1;
  readonly width: number;
}

/* A quay is quay from side to side: its bank is the side that meets the water. */
function quayOf(street: Street, isWater: (p: Vec2) => boolean): GoodsQuay {
  if (street.kind !== 'quay') throw new Error(`${street.id} is no quay to stack goods on`);
  const half = street.width / 2;
  const frame = streetFrame(street.centerline, half);
  const [left, right] = [1, -1].map((side) =>
    isWater(frame.at(frame.length / 2, side * (half + 1)))
  );
  if (left === right) throw new Error(`${street.id} has no water on one side to take as its bank`);
  const side = left ? 1 : -1;
  return {
    street: street.id,
    frame,
    bank: side * half,
    inward: side === 1 ? -1 : 1,
    width: street.width,
  };
}

interface Slot {
  readonly record: QuayGoodsRecord;
  readonly quay: GoodsQuay;
  readonly s: number;
  readonly t: number;
  readonly width: number;
}

/* The drawn place across the quay, moved just far enough to keep the whole item on it. An item drawn
   wider than the quay, as timbers reaching out over the water, is cut to the quay's room. */
function slotOf(record: QuayGoodsRecord, quay: GoodsQuay): Slot {
  const room = quay.width - BANK_CLEARANCE_M;
  const width = Math.min(record.width, room);
  const half = width / 2;
  const fromBank = Math.min(
    Math.max(record.across * quay.width, BANK_CLEARANCE_M + half),
    quay.width - half
  );
  const s = quay.frame.coordsOf(record.at)[0];
  return { record, quay, s, t: quay.bank + quay.inward * fromBank, width };
}

function overlaps(a: Slot, b: Slot): number {
  if (a.quay.street !== b.quay.street) return 0;
  const across = (a.width + b.width) / 2 - Math.abs(a.t - b.t);
  const along = (a.record.length + b.record.length) / 2 + PART_GAP_M - Math.abs(a.s - b.s);
  return across > 0 && along > 0 ? along : 0;
}

// Items the woodcut draws touching overlap by the georeference's error; each gives way by half.
function parted(slots: readonly Slot[]): Slot[] {
  const out = [...slots];
  const order = out.map((_, k) => k).sort((i, j) => out[i]!.s - out[j]!.s);
  order.forEach((i, n) => {
    order.slice(n + 1).forEach((j) => {
      const overlap = overlaps(out[i]!, out[j]!);
      if (overlap === 0) return;
      out[i] = { ...out[i]!, s: out[i]!.s - overlap / 2 };
      out[j] = { ...out[j]!, s: out[j]!.s + overlap / 2 };
    });
  });
  return out;
}

// The bank lies toward -inward in t; which side of the item's yaw that is.
function bankSideOf(quay: GoodsQuay, s: number, t: number, ahead: Vec2, behind: Vec2): 1 | -1 {
  const [centre, bank] = [quay.frame.at(s, t), quay.frame.at(s, t - quay.inward)];
  const along: Vec2 = [ahead[0] - behind[0], ahead[1] - behind[1]];
  const left = along[0] * (bank[1] - centre[1]) - along[1] * (bank[0] - centre[0]);
  return left > 0 ? -1 : 1;
}

function placed({ record, quay, s, t, width }: Slot): PlacedGoods {
  const [halfL, halfW] = [record.length / 2, width / 2];
  const ahead = quay.frame.at(s + halfL, t);
  const behind = quay.frame.at(s - halfL, t);
  return {
    id: record.id,
    street: quay.street,
    kind: record.kind,
    centre: quay.frame.at(s, t),
    yaw: Math.atan2(ahead[1] - behind[1], ahead[0] - behind[0]),
    length: record.length,
    width,
    height: record.height,
    ...(record.bar !== undefined ? { bar: record.bar } : {}),
    ...(record.beam !== undefined && record.fork !== undefined
      ? { beam: record.beam, fork: record.fork }
      : {}),
    bankSide: bankSideOf(quay, s, t, ahead, behind),
    footprint: [
      quay.frame.at(s - halfL, t - halfW),
      quay.frame.at(s + halfL, t - halfW),
      quay.frame.at(s + halfL, t + halfW),
      quay.frame.at(s - halfL, t + halfW),
    ],
  };
}

/**
 * Each item on its quay street, where it is drawn, whole on the quay's ground and clear of the others.
 * `isWater` finds the bank of a quay street with no strip.
 */
export function placeQuayGoods(
  records: readonly QuayGoodsRecord[],
  streets: readonly Street[],
  isWater: (p: Vec2) => boolean = () => false
): PlacedGoods[] {
  const quays = new Map<string, GoodsQuay>();
  const quayFor = (record: QuayGoodsRecord): GoodsQuay => {
    const sourceStreet = streets.find((s) => s.id === record.street);
    if (!sourceStreet) throw new Error(`no street ${record.street} for the quay goods`);
    // Numeric suffixes are generated segment indices, not persistent identities of the two banks.
    // Adding a period waterway can split one bank and renumber the other. Find the source reading's
    // nearest finite centre line in that family; extrapolating the old segment puts goods on lots.
    const stem = record.street.replace(/-\d+$/, '');
    const family = streets.filter(
      (s) =>
        s.name === sourceStreet.name &&
        s.kind === sourceStreet.kind &&
        s.id.replace(/-\d+$/, '') === stem
    );
    const distance = (s: Street): number =>
      Math.min(
        ...s.centerline
          .slice(1)
          .map((b, i) => distancePointSegment(record.at, { a: s.centerline[i]!, b }))
      );
    const street = family.reduce(
      (best, s) => (distance(s) < distance(best) ? s : best),
      sourceStreet
    );
    const known = quays.get(street.id);
    if (known) return known;
    const quay = quayOf(street, isWater);
    quays.set(street.id, quay);
    return quay;
  };
  return parted(records.map((record) => slotOf(record, quayFor(record)))).map(placed);
}
